import { IAppState, ILog, ILogAutoType, IShipStatus, IStatus, IStatusType, Log } from "../../Types"
import { Icons } from "../../Components/Icon"
import { UserAdviceLevel, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceStopOverlay } from "./StopView"

// Stable `provisionalKey` of the in-progress automatic-pause marker (#1218), mirroring
// Swift's `LogMetadata.speedChangeStopProvisionalKey`. It is a `static let` there, so
// TypeGen has no generated counterpart to import — the spelling just has to match, as
// it is only ever used as a map key (see `useDayEvents`/`AppData.updateProvisional`).
export const SPEED_CHANGE_STOP_PROVISIONAL_KEY = "speedChangeStop";

export class StopUserAdvice extends UserAdvice {
  readonly role = UserAdviceRole.action;
  active: boolean = false;
  started: number = 0;
  statusId?: number = undefined;
  fixedShipStatus?: IShipStatus = undefined;
  // The day's statuses as of the last update, handed to the dialog so it can spot
  // running manual engines without reaching for the day context (#1171).
  statuses: IStatus[] = [];
  static level: number = 10;

  update(_app: IAppState, state: FocusViewState, currentLog?: ILog) {
    this.active = false;
    this.statusId = undefined;
    this.fixedShipStatus = undefined;
    this.statuses = state.statuses;
    if (!currentLog) {
      return;
    }
    const shipStatus = UserAdvice.status(state.statuses, IStatusType.ship, currentLog.dayId).filter((s) => s.endLogId == undefined).toSorted((a, b) => b.id - a.id)[0];
    if (!shipStatus) {
      this.started = 0;
      return;
    }
    const startLog = state.logs.find((log) => log.id === shipStatus.startLogId);
    const manuallySelectedStopped = [
      IShipStatus.anchor,
      IShipStatus.buoy,
      IShipStatus.port,
      IShipStatus.drifting,
    ].includes(shipStatus.value) && startLog?.labels?.includes("manual");
    const hasStopHint = startLog && state.logs.some((entry) =>
      Log.parse(entry).dataParsed.type === ILogAutoType.stop &&
      (entry.timeUtc > startLog.timeUtc ||
        (entry.timeUtc === startLog.timeUtc && entry.id > startLog.id)),
    );
    if (manuallySelectedStopped) {
      if (!hasStopHint) {
        this.active = true;
        this.statusId = shipStatus.id;
        this.fixedShipStatus = shipStatus.value;
        this.started = startLog?.timeUtc ?? 0;
      } else {
        this.started = 0;
      }
      return;
    }
    if (shipStatus && shipStatus.value != IShipStatus.inmotion && shipStatus.value != IShipStatus.drifting) {
      this.started = 0;
      return;
    }
    // While the automatic stop is only pending (#1218) the ship status is still the
    // `.inmotion` one the pause interrupts, so the engine's live marker is the only
    // thing that knows a pause is under way -- and its time is the moment the ship
    // actually stopped, which is what the button's "Stopped for …" has to count from.
    // Prefer it over the local speed/status reading: it is the server's own verdict,
    // reached on `thresholds.stop` rather than a standstill guessed at here.
    const pendingStop = state.provisionalLogs?.[SPEED_CHANGE_STOP_PROVISIONAL_KEY];
    if (pendingStop && pendingStop.dayId == currentLog.dayId) {
      this.statusId = shipStatus.id;
      this.started = pendingStop.timeUtc;
      this.active = true;
      return;
    }
    // No live marker, so the only pause this can be is one that has already committed
    // -- which is precisely what a `.drifting` status is, and whose start log IS the
    // stop entry, so the duration below is measured from the right moment.
    //
    // The ship's live speed is deliberately NOT consulted (#1793). A committed pause is
    // a server-side fact -- the engine opened `.drifting` on its own `thresholds.stop`
    // -- so re-deciding it here against a hardcoded 0.1 kn only took the button away
    // from a ship lying at anchor whose GPS reads a few tenths of a knot, which is what
    // @almute hit ("Ich habe keinen Button gesehen"). It gated nothing else: an
    // `.inmotion` ship without a marker is already turned away just below.
    //
    // An `.inmotion` ship at a standstill with no marker is deliberately NOT offered
    // the stop. Either the engine has not broadcast the marker yet (at most one
    // process interval), or the crew dismissed the pending pause -- and a dismissal
    // means "don't tell me about this pause" until the ship moves again and stops
    // afresh. Activating here would also mis-time the button: the open status is the
    // one under which the ship got *under way*, so a four-hour passage would render as
    // "Stopped for 4:00:12" (the bug the marker above was introduced to fix, #1218).
    if (shipStatus.value != IShipStatus.drifting) {
      this.started = 0;
      return;
    }
    if (startLog) {
      this.statusId = shipStatus.id;
      this.started = startLog.timeUtc;
    } else {
      this.started = 0;
    }
    this.active = true;
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    if (!this.active || !currentLog || location != UserAdviceLocation.log) {
      return [];
    }
    const t = app.getT("UserAdviceStopView");
    const duration = currentLog.timeUtc - this.started;
    return [
      new UserAdviceInstance("stop", StopUserAdvice.level, { icon: Icons.AdviceStop, level: UserAdviceLevel.notice, shortTitle: t("title_short"), title: t(this.started ? "title_duration" : "title", { duration: app.format("time", duration) }), type: "stop" }, this, 0, location),
    ];
  }

  overlay(_app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    return userAdviceStopOverlay({
      advice,
      state,
      statuses: this.statuses,
      fixedShipStatus: this.fixedShipStatus,
    });
  }
}
